import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import FloatingNavigation from "../components/FloatingNavigation";

const GuideSalesDetailPage = () => {
  const { guideId } = useParams();
  const token = localStorage.getItem("token");

  const [salesData, setSalesData] = useState([]);
  const [filteredSalesData, setFilteredSalesData] = useState([]);
  const [guideInfo, setGuideInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [showBankDetails, setShowBankDetails] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [filters, setFilters] = useState({
    dateFilterType: "all",
    startDate: "",
    endDate: "",
    selectedSite: "",
    specificDate: "",
  });

  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/sites/active`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSites(response.data.sites || []);
    } catch (error) {
      console.error("Error fetching sites:", error);
      setSites([]);
    }
  }, [token]);

  const fetchGuideSales = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/guide-analytics/guide/${guideId}/sales`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSalesData(response.data.sales || []);
      setFilteredSalesData(response.data.sales || []);
    } catch (error) {
      console.error("Error fetching guide sales:", error);
      setSalesData([]);
      setFilteredSalesData([]);
    } finally {
      setLoading(false);
    }
  }, [guideId, token]);

  const fetchGuideDetails = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/get-guide-details/${guideId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setGuideInfo(response.data);
    } catch (error) {
      console.error("Error fetching guide details:", error);
    }
  }, [guideId, token]);

  const applyFilters = useCallback(() => {
    let filtered = [...salesData];

    if (filters.dateFilterType === "specific" && filters.specificDate) {
      filtered = filtered.filter((sale) => {
        const saleDate = new Date(sale.date || sale.createdAt);
        const filterDate = new Date(filters.specificDate);
        return saleDate.toDateString() === filterDate.toDateString();
      });
    } else if (
      filters.dateFilterType === "range" &&
      filters.startDate &&
      filters.endDate
    ) {
      filtered = filtered.filter((sale) => {
        const saleDate = new Date(sale.date || sale.createdAt);
        return (
          saleDate >= new Date(filters.startDate) &&
          saleDate <= new Date(filters.endDate)
        );
      });
    }

    if (filters.selectedSite) {
      filtered = filtered.filter((sale) =>
        sale.siteName &&
        sale.siteName.toLowerCase().includes(filters.selectedSite.toLowerCase())
      );
    }

    setFilteredSalesData(filtered);
    setCurrentPage(1);
  }, [salesData, filters]);

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
      ...(filterName === "dateFilterType"
        ? { specificDate: "", startDate: "", endDate: "" }
        : {}),
    }));
  };

  const clearFilters = () => {
    setFilters({
      dateFilterType: "all",
      startDate: "",
      endDate: "",
      selectedSite: "",
      specificDate: "",
    });
  };

  const exportToCSV = () => {
    const csvData = filteredSalesData.map((sale) => ({
      Date: sale.date || new Date(sale.createdAt).toLocaleDateString(),
      "Guide ID": sale.guideId || guideId,
      Tickets: sale.numberOfTickets || 0,
      "Total Price": sale.totalPrice || 0,
      Commission: sale.commission || sale.totalPrice * 0.2 || 0,
      "Site Name": sale.siteName || "N/A",
    }));

    const csvContent = [
      Object.keys(csvData[0] || {}).join(","),
      ...csvData.map((row) => Object.values(row).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `guide_${guideId}_sales_${new Date()
      .toISOString()
      .split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(
    filteredSalesData.length /
      (itemsPerPage === "all" ? filteredSalesData.length : itemsPerPage)
  );
  const startIndex =
    itemsPerPage === "all" ? 0 : (currentPage - 1) * itemsPerPage;
  const endIndex =
    itemsPerPage === "all"
      ? filteredSalesData.length
      : startIndex + itemsPerPage;
  const currentData = filteredSalesData.slice(startIndex, endIndex);

  useEffect(() => {
    if (guideId && token) {
      fetchGuideSales();
      fetchGuideDetails();
      fetchSites();
    }
  }, [guideId, token, fetchGuideSales, fetchGuideDetails, fetchSites]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading guide sales data...</p>
        </div>
      </div>
    );
  }

  if (!guideInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Guide details not available</h3>
          <p className="text-gray-600">Unable to fetch guide information. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <h1 className="text-2xl font-bold text-gray-900 mb-4 sm:mb-0">
              {guideInfo.guideName || "N/A"}'s Sales Details
            </h1>
            <button
              onClick={exportToCSV}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Export CSV
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Guide Information */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Guide Information</h2>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={showBankDetails}
                    onChange={(e) => setShowBankDetails(e.target.checked)}
                    className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
                  />
                  <span className="ml-2 text-sm text-gray-700">Show Bank Details</span>
                </label>
              </div>
              
              <div className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{guideInfo.guideName || "N/A"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">ID</dt>
                  <dd className="mt-1 text-sm text-gray-900">{guideInfo.guideId || "N/A"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Phone</dt>
                  <dd className="mt-1 text-sm text-gray-900">{guideInfo.phone || "N/A"}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900">{guideInfo.email || "N/A"}</dd>
                </div>
                
                {showBankDetails && (
                  <>
                    <hr className="my-4" />
                    <h3 className="text-md font-semibold text-gray-900 mb-3">Bank Details</h3>
                    <div className="space-y-3">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Bank</dt>
                        <dd className="mt-1 text-sm text-gray-900">{guideInfo.bankName || "N/A"}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Branch</dt>
                        <dd className="mt-1 text-sm text-gray-900">{guideInfo.branch || "N/A"}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Account Number</dt>
                        <dd className="mt-1 text-sm text-gray-900">{guideInfo.accountNumber || "N/A"}</dd>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Sales Records */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Sales Records</h2>
                  <span className="text-sm text-gray-500">
                    {filteredSalesData.length > 0
                      ? `${startIndex + 1}-${Math.min(endIndex, filteredSalesData.length)} of ${filteredSalesData.length} records`
                      : "No records found"}
                  </span>
                </div>
              </div>

              {/* Filters */}
              <div className="p-6 border-b border-gray-200 bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date Filter</label>
                    <select
                      value={filters.dateFilterType}
                      onChange={(e) => handleFilterChange("dateFilterType", e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="all">All Dates</option>
                      <option value="specific">Specific Date</option>
                      <option value="range">Date Range</option>
                    </select>
                  </div>

                  {filters.dateFilterType === "specific" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Select Date</label>
                      <input
                        type="date"
                        value={filters.specificDate}
                        onChange={(e) => handleFilterChange("specificDate", e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      />
                    </div>
                  )}

                  {filters.dateFilterType === "range" && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                          type="date"
                          value={filters.startDate}
                          onChange={(e) => handleFilterChange("startDate", e.target.value)}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                        <input
                          type="date"
                          value={filters.endDate}
                          onChange={(e) => handleFilterChange("endDate", e.target.value)}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Site</label>
                    <select
                      value={filters.selectedSite}
                      onChange={(e) => handleFilterChange("selectedSite", e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="">All Sites</option>
                      {sites.map((site) => (
                        <option key={site._id} value={site.siteName}>
                          {site.displayName || site.siteName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rows per Page</label>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        const value = e.target.value === "all" ? "all" : parseInt(e.target.value);
                        setItemsPerPage(value);
                        setCurrentPage(1);
                      }}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value="all">All</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <button
                    onClick={clearFilters}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                {filteredSalesData.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No sales records found</p>
                    <p className="text-sm text-gray-400 mt-1">Try adjusting your filters to see more results</p>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Guide ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tickets</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Price</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Site</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentData.map((sale, index) => (
                        <tr key={sale._id || index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {sale.date || new Date(sale.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {sale.guideId || guideId}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {sale.numberOfTickets || 0}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            Rs. {(sale.totalPrice || 0).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                            Rs. {(sale.commission || sale.totalPrice * 0.2 || 0).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {sale.siteName || "N/A"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Pagination */}
              {itemsPerPage !== "all" && totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{startIndex + 1}</span> to{" "}
                        <span className="font-medium">{Math.min(endIndex, filteredSalesData.length)}</span> of{" "}
                        <span className="font-medium">{filteredSalesData.length}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        {[...Array(Math.min(5, totalPages))].map((_, i) => {
                          const pageNum = Math.max(1, currentPage - 2) + i;
                          if (pageNum <= totalPages) {
                            return (
                              <button
                                key={pageNum}
                                onClick={() => setCurrentPage(pageNum)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                  currentPage === pageNum
                                    ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                    : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          }
                          return null;
                        })}
                        <button
                          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <FloatingNavigation />
    </div>
  );
};

export default GuideSalesDetailPage;
